import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeftRight, Check, X, LogIn, UserPlus } from "lucide-react";
import { api, formatApiError } from "../api";
import { ocrVinFromImageFile } from "../lib/vinOcr";
import { useAuth } from "../context/AuthContext";
import { fmtDate, fmtKm } from "../lib/labels";

export default function DevirKabul() {
  const { token } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const ruhsatRef = useRef(null);
  const [ruhsatUploading, setRuhsatUploading] = useState(false);
  const [ruhsatPath, setRuhsatPath] = useState("");
  const [ocrVin, setOcrVin] = useState("");
  const [ocrState, setOcrState] = useState(null);

  const pickRuhsat = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    if (!["image/jpeg", "image/png"].includes(f.type)) { toast.error("Sadece JPG veya PNG yükleyebilirsiniz"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Fotoğraf 5 MB'ı aşamaz"); return; }
    setRuhsatUploading(true);
    setOcrVin("");
    setOcrState(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      setRuhsatPath(data.path);
      setOcrState({ kind: "reading", text: "Ruhsat üzerindeki şasi numarası okunuyor..." });
      try {
        const found = await ocrVinFromImageFile(f);
        setOcrVin(found || "");
        const expected = (invite?.vehicle?.vin || "").trim().toUpperCase();
        if (!found) setOcrState({ kind: "unreadable", text: "Ruhsattan şasi numarası okunamadı; devir yönetici incelemesine gider." });
        else if (expected && found === expected) setOcrState({ kind: "ok", text: "Şasi numarası ruhsatla eşleşti — doğrulandı." });
        else if (expected) setOcrState({ kind: "mismatch", text: `Ruhsattaki şasi (${found}) araç kaydıyla eşleşmiyor; devir yönetici incelemesine gider.` });
        else setOcrState({ kind: "ok", text: `Şasi numarası ruhsattan okundu: ${found}` });
      } catch { setOcrState({ kind: "unreadable", text: "Ruhsat okunamadı; devir yönetici incelemesine gider." }); }
    } catch (err) { toast.error(formatApiError(err)); }
    setRuhsatUploading(false);
  };

  useEffect(() => {
    setInvite(null);
    setError("");
    api.get(`/devir/${token}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setError(formatApiError(e)));
  }, [token]);

  const act = async (action) => {
    if (action === "kabul" && !ruhsatPath) { toast.error("Devir için güncel ruhsat fotoğrafı yüklenmesi zorunludur (şasi doğrulaması için)."); return; }
    setBusy(true);
    try {
      await api.post(`/devir/${token}/${action}`, action === "kabul" ? { ruhsat: ruhsatPath, ocr_vin: ocrVin || "" } : undefined);
      toast.success(action === "kabul" ? "Devir tamamlandı. Araç artık hesabınızda." : "Devir daveti reddedildi");
      navigate(action === "kabul" ? "/araclarim" : "/");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div data-testid="devir-kabul-page" className="min-h-screen pt-24 pb-16">
      <div className="max-w-lg mx-auto px-4">
        <div className="rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-xl p-6 sm:p-8">
          <h1 className="text-2xl font-extrabold text-white flex items-center gap-2">
            <ArrowLeftRight className="w-6 h-6 text-cyan-300" /> Araç Devir Daveti
          </h1>
          {error ? (
            <p data-testid="devir-error" className="mt-4 text-sm text-red-400">{error}</p>
          ) : !invite ? (
            <div className="mt-8 flex justify-center">
              <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <div data-testid="devir-vehicle-info" className="mt-5 rounded-xl border border-white/10 bg-slate-950/50 p-5">
                <p className="text-xl font-extrabold text-white">
                  {invite.vehicle.brand} {invite.vehicle.model} <span className="text-slate-400 font-semibold text-base">{invite.vehicle.year}</span>
                </p>
                <p className="mt-1.5 text-sm text-slate-300">
                  {invite.vehicle.plate && <span className="font-bold">{invite.vehicle.plate} • </span>}{fmtKm(invite.vehicle.km)}
                </p>
                <p className="mt-2 text-sm text-slate-400">
                  Gönderen: <span className="text-slate-200 font-semibold">{invite.from_name}</span>
                </p>
                <p className="mt-1 text-xs text-slate-500">Davet geçerlilik: {fmtDate((invite.expires_at || "").slice(0, 10))}</p>
              </div>

              {invite.status !== "pending" ? (
                <p data-testid="devir-used" className="mt-5 text-sm text-amber-300">Bu devir daveti daha önce kullanıldı.</p>
              ) : invite.expired ? (
                <p data-testid="devir-expired" className="mt-5 text-sm text-red-300">Bu devir davetinin süresi dolmuş.</p>
              ) : !user ? (
                <div className="mt-6 space-y-3">
                  <p data-testid="devir-login-note" className="text-sm text-slate-300">
                    Deviri kabul etmek için bu davetin gönderildiği e-posta adresiyle giriş yapın veya kayıt olun.
                  </p>
                  <div className="flex gap-3">
                    <Link data-testid="devir-login-button" to="/giris" state={{ from: location.pathname }}
                      className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-cyan-400 text-slate-950 text-sm font-bold hover:bg-cyan-300 transition-colors">
                      <LogIn className="w-4 h-4" /> Giriş Yap
                    </Link>
                    <Link data-testid="devir-register-button" to="/kayit" state={{ from: location.pathname }}
                      className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border border-cyan-400/40 text-cyan-300 text-sm font-bold hover:bg-cyan-400/10 transition-colors">
                      <UserPlus className="w-4 h-4" /> Kayıt Ol
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="mt-6 space-y-4">
                  <div data-testid="devir-ruhsat-card" className="rounded-xl border border-white/10 bg-slate-950/50 p-4">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Ruhsat (Şasi Doğrulama) *</p>
                    <input ref={ruhsatRef} type="file" accept="image/jpeg,image/png" capture="environment" className="hidden" onChange={pickRuhsat} />
                    <div className="flex items-center gap-3">
                      <button type="button" data-testid="devir-ruhsat-btn" onClick={() => ruhsatRef.current?.click()}
                        disabled={ruhsatUploading}
                        className="px-4 py-2.5 rounded-xl border border-white/10 text-slate-300 text-xs font-semibold hover:border-cyan-400/50 hover:text-cyan-300 transition-colors disabled:opacity-50">
                        {ruhsatUploading ? "Yükleniyor..." : ruhsatPath ? "Ruhsatı Değiştir" : "Ruhsat Yükle (Kamera / Galeri)"}
                      </button>
                      {ruhsatPath && (
                        <span data-testid="devir-ruhsat-ok" className="text-xs font-semibold text-emerald-300">Ruhsat yüklendi</span>
                      )}
                      {ocrState && (
                        <span data-testid="devir-ruhsat-ocr" className={`text-xs font-semibold leading-snug ${ocrState.kind === "ok" ? "text-emerald-300" : ocrState.kind === "reading" ? "text-slate-400" : "text-amber-300"}`}>
                          {ocrState.text}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
                      Ruhsattaki şasi numarası araçla karşılaştırılır; eşleşirse yeni döneminiz "Doğrulandı" olur, eşleşmezse veya okunamazsa yönetici incelemesine gider. Ruhsat yüklenmeden devir tamamlanamaz.
                    </p>
                  </div>
                  <div className="flex gap-3">
                  <button data-testid="devir-accept-button" onClick={() => act("kabul")} disabled={busy || ruhsatUploading || !ruhsatPath}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-500 text-slate-950 text-sm font-bold hover:bg-emerald-400 transition-colors disabled:opacity-50">
                    <Check className="w-4 h-4" /> Deviri Kabul Et
                  </button>
                  <button data-testid="devir-reject-button" onClick={() => act("reddet")} disabled={busy}
                    className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-white/10 text-slate-300 text-sm font-semibold hover:border-red-400/50 hover:text-red-300 transition-colors disabled:opacity-50">
                    <X className="w-4 h-4" /> Reddet
                  </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
